import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, resolveImageSrc } from "@/lib/api";
import { S } from "./ibloom/styles";
import { productAlt } from "@/lib/seo";
import ShareButton from "./ibloom/mapbazzar/ShareButton";

/**
 * Public single-product page (no auth) at /product/{slug}. SEO landing page for
 * a single listing — full image, description, price and one-tap WhatsApp / Buy
 * Now CTAs. Old UUID links resolve too and client-canonicalise to the slug URL.
 */
export default function ProductPage() {
  const { slug, city, shopSlug, productSlug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const byPath = Boolean(city && shopSlug && productSlug);
    const url = byPath
      ? `/products/by-path/${city}/${shopSlug}/${productSlug}`
      : `/products/by-slug/${slug}`;
    api.get(url)
      .then((r) => {
        if (!active) return;
        setProduct(r.data?.product);
        setShop(r.data?.shop);
        const canonical = r.data?.seo_path;
        const current = byPath ? `/${city}/${shopSlug}/${productSlug}` : `/product/${slug}`;
        if (canonical && canonical !== current) navigate(canonical, { replace: true });
      })
      .catch(() => active && setErr("Product not found"))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [slug, city, shopSlug, productSlug, navigate]);

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: "#888" }}>Loading product…</div>;
  if (err || !product) return (
    <div style={{ padding: 40, textAlign: "center" }} data-testid="product-page-not-found">
      <div style={{ fontSize: 36, marginBottom: 8 }}>📦</div>
      <div style={{ fontSize: 14, color: "#888" }}>{err || "Product not found"}</div>
      <button onClick={() => navigate("/")} style={{ marginTop: 14, padding: "10px 18px", borderRadius: 100, border: "none", background: "#0F3D2E", color: "white", fontWeight: 700, cursor: "pointer" }}>Back to Mapbazar</button>
    </div>
  );

  const shopName = shop?.name || "Local Shop";
  const wa = (shop?.phone || "").replace(/\D/g, "");
  const waDigits = wa.length === 10 ? `91${wa}` : wa;
  const waMsg = `Hi ${shopName}! I saw "${product.name}" (₹${product.price}) on your Mapbazar catalogue. 🙏`;
  const waUrl = waDigits && waDigits.length >= 10 ? `https://wa.me/${waDigits}?text=${encodeURIComponent(waMsg)}` : null;
  const main = resolveImageSrc(product.image);
  const inStock = product.in_stock !== false && product.stock !== 0;

  return (
    <div style={{ maxWidth: 520, margin: "0 auto", minHeight: "100dvh", background: "#FAFAF5" }} data-testid="product-page">
      {main && (main.startsWith("http") || main.startsWith("/api/") || main.startsWith("data:")) ? (
        <img src={main} alt={productAlt(product.name, shopName, shop?.address)} data-testid="product-page-image"
          style={{ width: "100%", height: 320, objectFit: "cover", display: "block" }} />
      ) : (
        <div style={{ width: "100%", height: 240, background: "#F6FAF7", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 56, color: "#bbb" }}>📦</div>
      )}

      <div style={{ padding: 20 }}>
        <div style={{ fontSize: 10, letterSpacing: 1.5, color: "#C97B3C", fontWeight: 700, marginBottom: 4, textTransform: "uppercase" }}>
          {product.category || "Local"}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "start" }}>
          <h1 style={{ ...S.fraunces, fontSize: 24, fontWeight: 700, lineHeight: 1.2, margin: 0 }} data-testid="product-page-name">{product.name}</h1>
          {Number(product.mrp) > Number(product.price) ? (
            <div style={{ textAlign: "right", flexShrink: 0 }}>
              <div style={{ ...S.fraunces, fontSize: 26, fontWeight: 800, color: "#8B1A4A" }} data-testid="product-page-price">₹{product.price}</div>
              <div data-testid="product-page-mrp" data-no-translate style={{ fontSize: 13, color: "#9AA4B7", textDecoration: "line-through", lineHeight: 1 }}>₹{product.mrp}</div>
              <div data-testid="product-page-off" data-no-translate style={{ marginTop: 3, display: "inline-block", background: "#1C6B4A", color: "white", fontSize: 10, fontWeight: 900, padding: "1px 6px", borderRadius: 100 }}>{Math.round((1 - Number(product.price) / Number(product.mrp)) * 100)}% OFF</div>
            </div>
          ) : (
            <div style={{ ...S.fraunces, fontSize: 26, fontWeight: 800, color: "#0F3D2E" }} data-testid="product-page-price">₹{product.price}</div>
          )}
        </div>
        {product.pack_size && (
          <div data-testid="product-page-pack-size" data-no-translate style={{ display: "inline-block", fontSize: 11.5, fontWeight: 800, color: "#0F3D2E", background: "#F0EFE8", borderRadius: 100, padding: "3px 10px", marginTop: 8 }}>📦 {product.pack_size}</div>
        )}
        {product.description && (
          <p style={{ fontSize: 13.5, color: "#555", lineHeight: 1.6, marginTop: 10 }}>{product.description}</p>
        )}

        <div style={{ marginTop: 16, padding: 14, background: "#FFF9F0", borderRadius: 12, border: "1px solid #F3CC8B" }}>
          <div style={{ fontSize: 10, letterSpacing: 1.5, color: "#888", fontWeight: 700, marginBottom: 4 }}>SOLD BY</div>
          <a href={shop?.seo_path || (shop?.slug ? `/shop/${shop.slug}` : "#")} data-testid="product-page-shop-link"
            style={{ fontSize: 15, fontWeight: 700, color: "#0F3D2E", textDecoration: "underline", textUnderlineOffset: 3 }}>
            {shopName} →
          </a>
          <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>📍 {shop?.address || "Ahmedabad"}</div>
        </div>

        <button
          data-testid="product-page-buy-now-btn"
          onClick={() => { try { sessionStorage.setItem("pending_buy_product_id", product.id); } catch (_) {} navigate("/"); }}
          disabled={!inStock}
          style={{
            marginTop: 14, display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            width: "100%", padding: 14, borderRadius: 100, border: "none",
            background: inStock ? "#0F3D2E" : "#999", color: "white", fontWeight: 800, fontSize: 14,
            cursor: inStock ? "pointer" : "not-allowed",
          }}
        >
          {inStock ? `🛵 Buy Now · ₹${product.price} COD · 30-min delivery` : "🚫 Currently out of stock"}
        </button>

        {waUrl && (
          <a href={waUrl} target="_blank" rel="noopener noreferrer" data-testid="product-page-whatsapp-btn"
            style={{ marginTop: 10, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%", padding: 12, borderRadius: 100, border: "1px solid #25D366", background: "white", color: "#1A7F4E", fontWeight: 700, fontSize: 13, textDecoration: "none" }}>
            💬 Chat with {shopName.split(" ")[0]} on WhatsApp
          </a>
        )}

        <ShareButton product={{ ...product, shop_id: shop?.id, shop_name: shopName }} variant="pill" testIdSuffix="product-page" />

        <button onClick={() => navigate("/")} style={{ marginTop: 8, width: "100%", padding: 12, borderRadius: 100, border: "1px solid #D5D3C7", background: "white", color: "#333", fontWeight: 600, cursor: "pointer" }}>
          → Explore more on Mapbazar
        </button>
      </div>
    </div>
  );
}
